import React, { useEffect, useRef } from 'react';
import QRCode from 'qrcode';

const PRINT_STYLE = `
@media print {
  body * { visibility: hidden !important; }
  #patient-card-print, #patient-card-print * { visibility: visible !important; }
  #patient-card-print {
    position: fixed !important;
    top: 0 !important; left: 0 !important;
    width: 85.6mm !important; height: 54mm !important;
    margin: 0 !important; padding: 0 !important;
    box-shadow: none !important;
  }
}
`;

export default function PatientCard({ patient, onClose }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (canvasRef.current && patient?.id) {
            const url = `http://${window.location.hostname}:3000/patients/${patient.id}`;
            QRCode.toCanvas(canvasRef.current, url, { width: 64, margin: 1, color: { dark: '#000000', light: '#ffffff' } });
        }
    }, [patient]);

    const formatDOB = (dob) => {
        if (!dob) return '—';
        const d = new Date(dob);
        return d.toLocaleDateString('en-PK', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const regDate = patient?.created_at
        ? new Date(patient.created_at).toLocaleDateString('en-PK', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : new Date().toLocaleDateString('en-PK', { day: '2-digit', month: '2-digit', year: 'numeric' });

    const bloodColor = {
        'A+': '#dc2626', 'A-': '#b91c1c',
        'B+': '#dc2626', 'B-': '#b91c1c',
        'AB+': '#7c3aed', 'AB-': '#6d28d9',
        'O+': '#0369a1', 'O-': '#1d4ed8',
    }[patient?.blood_group] || '#dc2626';

    return (
        <>
            <style>{PRINT_STYLE}</style>

            {/* Modal Overlay */}
            <div style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 9999, flexDirection: 'column', gap: 20
            }}>
                {/* Card */}
                <div id="patient-card-print" style={{
                    width: 340, height: 215,
                    borderRadius: 13,
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)',
                    padding: '18px 20px',
                    position: 'relative', overflow: 'hidden',
                    boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}>
                    {/* Decorative circles */}
                    <div style={{ position: 'absolute', right: -40, top: -40, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
                    <div style={{ position: 'absolute', left: -25, bottom: -35, width: 110, height: 110, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />

                    {/* Top Row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 800, color: 'white', letterSpacing: 0.3 }}>🏥 {process.env.REACT_APP_HOSPITAL_NAME || 'City General Hospital'}</div>
                            <div style={{ fontSize: 8, color: '#93c5fd', marginTop: 2, letterSpacing: 1.2, textTransform: 'uppercase' }}>Patient Registration Card</div>
                        </div>
                        <div style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 7, padding: '5px 10px', textAlign: 'right' }}>
                            <div style={{ fontSize: 7, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: 1 }}>MRN No.</div>
                            <div style={{ color: 'white', fontSize: 12, fontWeight: 900, fontFamily: 'monospace', letterSpacing: 1 }}>{patient?.mrn}</div>
                        </div>
                    </div>

                    <div style={{ height: 1, background: 'rgba(255,255,255,0.1)', marginBottom: 12 }} />

                    {/* Body */}
                    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                        {/* Info Grid */}
                        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
                            <div>
                                <div style={{ fontSize: 7, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>Patient Name</div>
                                <div style={{ color: 'white', fontSize: 12, fontWeight: 600 }}>{patient?.full_name}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 7, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>Date of Birth</div>
                                <div style={{ color: 'white', fontSize: 12, fontWeight: 600 }}>{formatDOB(patient?.date_of_birth)}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 7, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>Phone</div>
                                <div style={{ color: 'white', fontSize: 12, fontWeight: 600 }}>{patient?.phone || '—'}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 7, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>Gender</div>
                                <div style={{ color: 'white', fontSize: 12, fontWeight: 600 }}>{patient?.gender || '—'}</div>
                            </div>
                        </div>

                        {/* QR + Blood Group */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                            <div style={{ width: 72, height: 72, background: 'white', borderRadius: 7, padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
                                <canvas ref={canvasRef} width={64} height={64} style={{ display: 'block' }} />
                            </div>
                            <div style={{ fontSize: 7, color: '#93c5fd', textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.5 }}>Scan for<br />Patient Record</div>
                            {patient?.blood_group && (
                                <div style={{ width: 38, height: 38, borderRadius: '50%', background: bloodColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', boxShadow: `0 3px 10px ${bloodColor}88`, border: '2px solid rgba(255,255,255,0.2)' }}>
                                    <span style={{ color: 'white', fontSize: 11, fontWeight: 900, lineHeight: 1 }}>{patient.blood_group}</span>
                                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 6, marginTop: 1 }}>Blood</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 8, color: '#475569' }}>📅 Registered: {regDate}</span>
                        <span style={{ fontSize: 8, color: '#64748b' }}>Keep this card safe — bring every visit</span>
                    </div>
                </div>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: 12 }}>
                    <button
                        onClick={() => window.print()}
                        style={{ padding: '11px 28px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        🖨️ Print Card
                    </button>
                    <button
                        onClick={onClose}
                        style={{ padding: '11px 20px', background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        ✕ Close
                    </button>
                </div>
            </div>
        </>
    );
}
